import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';

import {
    Label,
    Text,
    FormField,
    Spacer,
} from '@zillow/constellation';

import {
    Col,
    Row,
} from '@zillow/zrs-comet';

import NotificationConstants from 'app/shared/constants/NotificationConstants';
import SchedulerActions from 'app/shared/actions/SchedulerActions';
import {
    StyledUppercaseText,
    StyledInlineSpacer,
    StyledSchedulerButton,
    StyledSchedulerDaySelect,
} from './StyledComponents';

const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

const timesOfDay = [
    'morning',
    'afternoon',
    'evening',
];

const propTypes = {
    isInputFormDisabled: PropTypes.bool.isRequired,
    listingAlias: PropTypes.string.isRequired,
    schedule: PropTypes.array,
    updateFormData: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

const defaultProps = {
    schedule: [],
};

const Scheduler = (props) => {
    const dispatch = useDispatch();
    const {
        isInputFormDisabled,
        listingAlias,
        schedule,
        updateFormData,
        updateStatusMessage,
    } = props;

    const initScheduleDisplay = () => {
        const udpatedSchedule = {};

        schedule.forEach((dayItem) => {
            udpatedSchedule[dayItem.day] = dayItem.morning || dayItem.afternoon || dayItem.evening;
        });

        return udpatedSchedule;
    };

    const [scheduleDisplay, setScheduleDisplay] = useState(initScheduleDisplay());

    const saveScheduleData = (scheduleData) => {
        updateStatusMessage('Saving...');

        return dispatch(SchedulerActions.update(listingAlias, scheduleData))
            .then((res) => {
                updateFormData({ schedule: res.payload.schedule });
                updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
            })
            .catch(() => {
                updateStatusMessage('There was an issue saving your changes.', NotificationConstants.ERROR);
            });
    };

    const handleDaySelect = ({ target: { value } }) => setScheduleDisplay({ ...scheduleDisplay, [value]: true });

    const handleTimeOfDaySelect = (day, timeOfDay) => {
        const scheduleCopy = schedule.slice(0);
        const dayToChange = scheduleCopy.find(dayItem => dayItem.day === day);

        dayToChange[timeOfDay] = !dayToChange[timeOfDay];
        saveScheduleData({ schedule: [dayToChange]});
    };

    const renderDayItem = (dayItem, index) => {
        const { day } = dayItem;

        return (
            <Row paddingY="xs" key={index} className="Scheduler-day-item">
                <Col md={2}>
                    <StyledUppercaseText fontType="bodySmall" as="div">
                        {`${day}s`}
                    </StyledUppercaseText>
                </Col>
                <Col
                    style={{ flexFlow: 'row wrap' }}
                    xs={12}
                    md={10}
                >
                    {timesOfDay.map((timeOfDay, i) => (
                        <StyledInlineSpacer key={`scheduler-button-spacer-${i}`} marginRight="xs">
                            <StyledSchedulerButton
                                type={dayItem[timeOfDay] ? 'outlineFill' : 'outline'}
                                onClick={() => handleTimeOfDaySelect(day, timeOfDay)}
                                key={`scheduler-button-${i}`}
                                rounded={true}
                                disabled={isInputFormDisabled}
                            >
                                {capitalize(timeOfDay)}
                            </StyledSchedulerButton>
                        </StyledInlineSpacer>
                    ))}
                </Col>
            </Row>
        );
    };

    return (
        <div className="Scheduler">
            <Text
                fontType="h4"
                as="div"
                marginBottom="xs"
                mediaQuery={{
                    lg: {
                        marginBottom: 'sm',
                    },
                }}
            >
                Tour availability
            </Text>
            {isEmpty(schedule) ?
                (
                    <Text fontType="body">
                        Sorry, the schedule service is currently unavailable. Please try again later.
                    </Text>
                ) : (
                    <>
                        <FormField>
                            <Label>Select the days you&apos;re available to show the property</Label>
                            <StyledSchedulerDaySelect
                                aria-label="Add a day"
                                className="Scheduler-day-select"
                                onChange={handleDaySelect}
                                disabled={isInputFormDisabled}
                                marginBottom="sm"
                            >
                                <option value="" defaultValue={true}>
                                    Add a day
                                </option>
                                {
                                    daysOfWeek.map((day, index) => (
                                        !scheduleDisplay[day] && (
                                            <option
                                                key={index}
                                                value={day}
                                            >
                                                { capitalize(day) }
                                            </option>
                                        )))
                                }
                            </StyledSchedulerDaySelect>
                        </FormField>
                        {schedule.map((dayItem, index) => (
                            scheduleDisplay[dayItem.day] && renderDayItem(dayItem, index)
                        ))}
                    </>
                )
            }
            <Spacer marginY="lg" />
        </div>
    );
};

Scheduler.propTypes = propTypes;
Scheduler.defaultProps = defaultProps;

export default Scheduler;
